import { useEffect, useState } from "react"
import { Chevron } from "@/components/Accordion/Accordion"
import { BOARD_FULL_NUDGE, PROMPTS_COPY, facilitatorPrompts, nudge } from "@/content/prompts"
import { useDebouncedField } from "@/hooks/useDebouncedField"
import { useFirestoreWrite } from "@/hooks/useFirestoreWrite"
import { getSessionNotes, setSessionNote } from "@/services/sessions"
import { useSessionStore } from "@/store/sessionStore"
import { selectJob, selectSession, unplacedCards } from "@/store/selectors"
import type { Round, SessionNotes } from "@/types"
import styles from "@/styles/components/FacilitatorPanel.module.css"

/*
 * The facilitator's rail: what to say, what this beat is for, and somewhere to
 * write.
 *
 * Only ever rendered for the facilitator, and that is not a permission detail —
 * the prompts give away what the round is testing ("note who writes nothing"),
 * so a guest seeing them would change how they play.
 *
 * The notes live at `sessions/{id}/private/notes`, one entry per round, and they
 * really are private: the session document is world-readable — its id is the
 * share link — so a note kept on it would be readable by the room it is written
 * about. The subcollection is facilitator-only in `firestore.rules`.
 *
 * They are fetched here rather than subscribed to in `sessionSync`, because this
 * panel is the only reader and a listener opened for every visitor would fail
 * for guests and take the session's connection down with it. One read on mount,
 * and the local copy is updated as it is typed — nobody else is writing it.
 */

interface FacilitatorPanelProps {
  canDrive: boolean
}

export default function FacilitatorPanel({ canDrive }: FacilitatorPanelProps) {
  const session = useSessionStore(selectSession)
  const job = useSessionStore(selectJob)
  const [open, setOpen] = useState(true)
  /*
   * Tagged with the session they came from, rather than held beside a "loaded"
   * flag: the tag answers both questions at once — whether the fetch has landed,
   * and whether what landed belongs to the session on screen. An empty object
   * cannot, because "nothing written yet" and "nothing fetched yet" look the
   * same, and the first note of a session would be typed into a box the fetch
   * then wipes.
   */
  const [fetched, setFetched] = useState<{ sessionId: string; notes: SessionNotes } | null>(null)

  const sessionId = session?.id ?? null
  const notes = fetched?.sessionId === sessionId ? fetched.notes : null

  useEffect(() => {
    if (!canDrive || sessionId === null) return
    let live = true

    getSessionNotes(sessionId)
      /*
       * A refused read does not deserve a screen of its own — it means this
       * browser cannot drive the session, and the panel it is on is already
       * facilitator-only. The box opens empty and the first write says why.
       */
      .catch((): SessionNotes => ({}))
      .then((loaded) => {
        if (live) setFetched({ sessionId, notes: loaded })
      })

    return () => {
      live = false
    }
  }, [canDrive, sessionId])

  // The lobby has the rules panel in this space, and the rail would fight it.
  if (!session || !canDrive || session.round === "lobby") return null

  /*
   * The Priorities round has one phase but two jobs: getting cards onto the
   * board, then reading a full board back to the room. The nudge has to change
   * even though the phase does not, so the screen state decides it.
   */
  const boardFull = session.round === "priorities" && unplacedCards(job).length === 0
  const thisBeat = boardFull ? BOARD_FULL_NUDGE : nudge(session.round, session.phase)

  if (!open) {
    return (
      <div className={styles.shut}>
        <button
          type="button"
          className={styles.reopen}
          title={PROMPTS_COPY.show}
          aria-label={PROMPTS_COPY.show}
          aria-expanded={false}
          onClick={() => setOpen(true)}
        >
          <Chevron open={false} />
        </button>
        <span className={styles.shutLabel}>{PROMPTS_COPY.panelLabel}</span>
      </div>
    )
  }

  return (
    <aside className={styles.panel} aria-label={PROMPTS_COPY.panelLabel}>
      <div className={styles.head}>
        <span className={styles.headLabel}>{PROMPTS_COPY.panelLabel}</span>
        <button
          type="button"
          className={styles.collapse}
          title={PROMPTS_COPY.hide}
          aria-label={PROMPTS_COPY.hide}
          aria-expanded
          onClick={() => setOpen(false)}
        >
          <Chevron open />
        </button>
      </div>

      <section className={styles.block}>
        <span className={styles.label}>{PROMPTS_COPY.nudgeLabel}</span>
        <p className={styles.nudge}>{thisBeat}</p>
      </section>

      <section className={styles.block}>
        <span className={styles.label}>{PROMPTS_COPY.promptsLabel}</span>
        <ul className={styles.prompts}>
          {facilitatorPrompts(session.round).map((prompt) => (
            <li key={prompt} className={styles.prompt}>
              {prompt}
            </li>
          ))}
        </ul>
      </section>

      <section className={`${styles.block} ${styles.notesBlock}`}>
        <span className={styles.label}>{PROMPTS_COPY.notesLabel}</span>
        {/*
         * Keyed by round so it remounts when the room moves on. Without that,
         * `useDebouncedField` keeps its draft across the change and commits the
         * previous round's half-typed note against the new round's field —
         * losing one note and overwriting another. Remounting flushes the
         * pending text through the closure that still names the old round.
         */}
        <RoundNotes
          key={session.round}
          sessionId={session.id}
          round={session.round}
          value={notes?.[session.round] ?? ""}
          loading={notes === null}
          onCommitted={(text) =>
            setFetched({
              sessionId: session.id,
              notes: { ...notes, [session.round]: text },
            })
          }
        />
      </section>
    </aside>
  )
}

function RoundNotes({
  sessionId,
  round,
  value,
  loading,
  onCommitted,
}: {
  sessionId: string
  round: Round
  value: string
  loading: boolean
  onCommitted: (text: string) => void
}) {
  const { write } = useFirestoreWrite()
  const note = useDebouncedField({
    value,
    /*
     * The committed text goes back to the panel as well as to Firestore. There
     * is no listener on this document, so nothing else would ever update
     * `value` — and the note would be gone from the box the next time the panel
     * handed the field back, or as soon as the round change remounted it.
     */
    onCommit: (text) => {
      onCommitted(text)
      write(() => setSessionNote(sessionId, round, text))
    },
  })

  return (
    <textarea
      className={styles.notes}
      placeholder={PROMPTS_COPY.notesPlaceholder}
      // Typing before the fetch lands would be overwritten by it.
      disabled={loading}
      value={note.value}
      onChange={note.onChange}
      onBlur={note.onBlur}
    />
  )
}
